import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Section } from "@/components/Primitives";
import { toast } from "sonner";
import { UserPlus, Trash, PencilSimpleLine, FloppyDisk } from "@phosphor-icons/react";

const ROLES = ["super_admin", "management", "regional_manager", "brand_manager", "viewer"];

// Keep in sync with backend MODULES in routes_auth.py
const MODULES = [
  { k: "dashboard", l: "Dashboard" },
  { k: "monthly_sales", l: "Monthly Sales" },
  { k: "reports", l: "Reports" },
  { k: "targets", l: "Targets" },
  { k: "chat", l: "Analyst AI" },
  { k: "upload", l: "Upload Center" },
  { k: "data_fixer", l: "Data Fixer" },
  { k: "export", l: "Export" },
  { k: "email", l: "Email Reports" },
  { k: "admin", l: "Admin" },
  { k: "audit", l: "Audit Logs" },
];

const emptyPerms = () => MODULES.reduce((a, m) => { a[m.k] = { read: true, write: false }; return a; }, {});

const PermissionMatrix = ({ value, onChange, testId = "perm-matrix" }) => {
  const perms = value || emptyPerms();
  const set = (modKey, axis, v) => {
    const next = { ...perms, [modKey]: { ...(perms[modKey] || { read: false, write: false }), [axis]: v } };
    // writing implies reading
    if (axis === "write" && v) next[modKey].read = true;
    // revoking read revokes write
    if (axis === "read" && !v) next[modKey].write = false;
    onChange(next);
  };
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between">
        <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Module permissions</label>
        <div className="flex items-center gap-1.5 text-[9px]">
          <button type="button" onClick={() => onChange(MODULES.reduce((a, m) => { a[m.k] = { read: true, write: true }; return a; }, {}))}
                  data-testid={`${testId}-all`}
                  className="uppercase tracking-wider px-2 py-0.5 border border-emerald-200 text-emerald-700 rounded-sm hover:bg-emerald-50">All on</button>
          <button type="button" onClick={() => onChange(MODULES.reduce((a, m) => { a[m.k] = { read: false, write: false }; return a; }, {}))}
                  data-testid={`${testId}-none`}
                  className="uppercase tracking-wider px-2 py-0.5 border border-rose-200 text-rose-700 rounded-sm hover:bg-rose-50">None</button>
        </div>
      </div>
      <div className="mt-2 border hairline rounded-sm overflow-hidden">
        <div className="grid grid-cols-[1fr_60px_60px] text-[10px] uppercase tracking-wider bg-myb-50 text-myb-500 font-semibold">
          <div className="px-3 py-1.5">Module</div>
          <div className="px-2 py-1.5 text-center">Read</div>
          <div className="px-2 py-1.5 text-center">Write</div>
        </div>
        {MODULES.map((m) => {
          const p = perms[m.k] || { read: false, write: false };
          return (
            <div key={m.k} className="grid grid-cols-[1fr_60px_60px] items-center border-t hairline text-xs hover:bg-myb-50/40">
              <div className="px-3 py-2">{m.l}</div>
              <label className="flex justify-center cursor-pointer">
                <input type="checkbox" data-testid={`${testId}-${m.k}-read`}
                       checked={!!p.read} onChange={(e) => set(m.k, "read", e.target.checked)}
                       className="accent-myb-500 w-3.5 h-3.5" />
              </label>
              <label className="flex justify-center cursor-pointer">
                <input type="checkbox" data-testid={`${testId}-${m.k}-write`}
                       checked={!!p.write} onChange={(e) => set(m.k, "write", e.target.checked)}
                       className="accent-myb-500 w-3.5 h-3.5" />
              </label>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-muted-foreground mt-1.5">Toggling “Write” automatically grants “Read”. Super admin always has full access regardless of this matrix.</div>
    </div>
  );
};

const emptyForm = { name: "", email: "", password: "", role: "viewer",
                    allowed_brands: [], allowed_companies: [],
                    allowed_locations: [], allowed_salesmen: [],
                    module_permissions: null };

const MultiChip = ({ label, testId, options, labelKey, valueKey, selected, onChange }) => {
  const [q, setQ] = useState("");
  const lower = q.toLowerCase();
  const shown = options.filter((o) => !q || (o[labelKey] || "").toLowerCase().includes(lower) || String(o[valueKey]).includes(lower));
  const toggle = (v) => {
    const s = new Set(selected);
    if (s.has(v)) s.delete(v); else s.add(v);
    onChange(Array.from(s));
  };
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{label}</label>
        <span className="text-[10px] text-myb-500">{selected.length ? `${selected.length} selected` : "All"}</span>
      </div>
      <input
        data-testid={`${testId}-search`}
        value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search…"
        className="mt-1 w-full bg-white border border-border focus:border-myb-500 rounded-sm px-3 py-2 text-sm outline-none"
      />
      <div className="mt-2 max-h-40 overflow-auto border hairline rounded-sm scrollbar-thin p-2 flex flex-wrap gap-1">
        {shown.slice(0, 150).map((o) => {
          const v = String(o[valueKey]);
          const active = selected.includes(v);
          return (
            <button
              type="button"
              key={v}
              data-testid={`${testId}-chip-${v}`}
              onClick={() => toggle(v)}
              className={`text-[11px] px-2 py-1 rounded-full border transition-all ${
                active ? "bg-myb-500 text-white border-myb-500"
                       : "border-myb-100 text-muted-foreground hover:border-myb-500 hover:text-myb-500"
              }`}
            >
              {o[labelKey] || v}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null); // id of user being edited
  const [editForm, setEditForm] = useState(null);

  const loadRefs = async () => {
    const [b, co, l, s] = await Promise.all([
      api.get("/ref/brands"), api.get("/ref/companies"),
      api.get("/ref/locations"), api.get("/ref/salesmen"),
    ]);
    setBrands(b.data.rows || []);
    setCompanies(co.data.rows || []);
    setLocations(l.data.rows || []);
    setSalesmen(s.data.rows || []);
  };
  const loadUsers = () => api.get("/admin/users").then((r) => setUsers(r.data || []));
  useEffect(() => { loadUsers(); loadRefs(); }, []);

  const addUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setForm(emptyForm);
      loadUsers();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Deleted"); loadUsers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startEdit = (u) => {
    setEditing(u.id);
    setEditForm({
      name: u.name, role: u.role, status: u.status, password: "",
      allowed_brands: u.allowed_brands || [],
      allowed_companies: u.allowed_companies || [],
      allowed_locations: u.allowed_locations || [],
      allowed_salesmen: u.allowed_salesmen || [],
      module_permissions: u.module_permissions || null,
    });
  };

  const saveEdit = async () => {
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      await api.patch(`/admin/users/${editing}`, payload);
      toast.success("User updated");
      setEditing(null); setEditForm(null);
      loadUsers();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Admin"
        title="Who runs the show"
        subtitle="Manage team access and per-user scope across brands, locations, and salesmen."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" title="Users" testId="admin-users">
          <div className="overflow-x-auto scrollbar-thin -mx-2 sm:mx-0">
            <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left border-b hairline">
                {["Name", "Email", "Role", "Scope", "Status", ""].map((h) => (
                  <th key={h} className="py-2 px-3 text-[10px] uppercase tracking-[0.2em] text-myb-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b hairline hover:bg-myb-50/50">
                  <td className="py-2 px-3">{u.name}</td>
                  <td className="py-2 px-3 text-muted-foreground">{u.email}</td>
                  <td className="py-2 px-3 text-myb-500 uppercase text-xs tracking-wider font-semibold">{u.role.replace("_", " ")}</td>
                  <td className="py-2 px-3 text-xs text-muted-foreground">
                    {u.role === "super_admin" ? (
                      <span className="text-myb-500">full</span>
                    ) : (
                      <span>
                        {u.allowed_companies?.length || 0}Co · {u.allowed_brands?.length || 0}B · {u.allowed_locations?.length || 0}L · {u.allowed_salesmen?.length || 0}S
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-emerald-600 text-xs">{u.status}</td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    <button
                      data-testid={`admin-edit-${u.id}`}
                      onClick={() => startEdit(u)}
                      className="text-myb-500 hover:text-myb-600 p-1 mr-1"
                      title="Edit"
                    ><PencilSimpleLine size={14} /></button>
                    <button
                      data-testid={`admin-delete-${u.id}`}
                      onClick={() => deleteUser(u.id)}
                      className="text-rose-500/80 hover:text-rose-600 p-1"
                      title="Delete"
                    ><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>

        <Card title="Create user" testId="admin-create-user">
          <form onSubmit={addUser} className="space-y-3">
            {[
              { k: "name", l: "Full name" },
              { k: "email", l: "Email", type: "email" },
              { k: "password", l: "Password", type: "password" },
            ].map((f) => (
              <div key={f.k}>
                <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{f.l}</label>
                <input
                  data-testid={`admin-create-${f.k}`}
                  required type={f.type || "text"}
                  value={form[f.k]}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  className="mt-1 w-full bg-white border border-border focus:border-myb-500 rounded-sm px-3 py-2.5 text-sm outline-none"
                />
              </div>
            ))}
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Role</label>
              <select
                data-testid="admin-create-role"
                value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="mt-1 w-full bg-white border border-border rounded-sm px-3 py-2.5 text-sm outline-none"
              >
                {ROLES.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
              </select>
            </div>
            {form.role !== "super_admin" && (
              <>
                <MultiChip label="Allowed companies (parent)" testId="admin-create-companies" options={companies}
                           labelKey="name" valueKey="code"
                           selected={form.allowed_companies}
                           onChange={(v) => setForm({ ...form, allowed_companies: v })} />
                <MultiChip label="Allowed brands" testId="admin-create-brands" options={brands}
                           labelKey="name" valueKey="name"
                           selected={form.allowed_brands}
                           onChange={(v) => setForm({ ...form, allowed_brands: v })} />
                <MultiChip label="Allowed locations" testId="admin-create-locations" options={locations}
                           labelKey="name" valueKey="code"
                           selected={form.allowed_locations}
                           onChange={(v) => setForm({ ...form, allowed_locations: v })} />
                <MultiChip label="Allowed salesmen" testId="admin-create-salesmen" options={salesmen}
                           labelKey="name" valueKey="code"
                           selected={form.allowed_salesmen}
                           onChange={(v) => setForm({ ...form, allowed_salesmen: v })} />
                <PermissionMatrix
                  testId="admin-create-perms"
                  value={form.module_permissions}
                  onChange={(v) => setForm({ ...form, module_permissions: v })}
                />
                <div className="text-[11px] text-muted-foreground">Leave scope empty to grant full access within the role. Permission matrix defaults to role defaults if untouched.</div>
              </>
            )}
            <button
              data-testid="admin-create-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-myb-500 text-white hover:bg-myb-600 rounded-sm py-2.5 text-sm"
            >
              <UserPlus size={14} /> Create user
            </button>
          </form>
        </Card>
      </div>

      {/* Edit drawer */}
      {editing && editForm && (
        <div className="fixed inset-0 z-40 flex" data-testid="admin-edit-panel">
          <div className="flex-1 bg-black/40" onClick={() => { setEditing(null); setEditForm(null); }} />
          <div className="w-full max-w-lg bg-white h-full overflow-auto scrollbar-thin shadow-2xl border-l hairline">
            <div className="p-5 border-b hairline sticky top-0 bg-white z-10">
              <div className="text-[10px] uppercase tracking-[0.3em] text-myb-500 font-semibold">Edit user</div>
              <div className="font-display text-xl font-light mt-1">{editForm.name}</div>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Name</label>
                <input
                  data-testid="admin-edit-name"
                  value={editForm.name || ""}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="mt-1 w-full bg-white border border-border focus:border-myb-500 rounded-sm px-3 py-2.5 text-sm outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Role</label>
                <select
                  data-testid="admin-edit-role"
                  value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="mt-1 w-full bg-white border border-border rounded-sm px-3 py-2.5 text-sm outline-none"
                >
                  {ROLES.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Status</label>
                <select
                  data-testid="admin-edit-status"
                  value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="mt-1 w-full bg-white border border-border rounded-sm px-3 py-2.5 text-sm outline-none"
                >
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Reset password (optional)</label>
                <input
                  data-testid="admin-edit-password"
                  type="password" value={editForm.password || ""}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder="Leave blank to keep current"
                  className="mt-1 w-full bg-white border border-border focus:border-myb-500 rounded-sm px-3 py-2.5 text-sm outline-none"
                />
              </div>
              {editForm.role !== "super_admin" && (
                <>
                  <MultiChip label="Allowed companies (parent)" testId="admin-edit-companies" options={companies}
                             labelKey="name" valueKey="code"
                             selected={editForm.allowed_companies}
                             onChange={(v) => setEditForm({ ...editForm, allowed_companies: v })} />
                  <MultiChip label="Allowed brands" testId="admin-edit-brands" options={brands}
                             labelKey="name" valueKey="name"
                             selected={editForm.allowed_brands}
                             onChange={(v) => setEditForm({ ...editForm, allowed_brands: v })} />
                  <MultiChip label="Allowed locations" testId="admin-edit-locations" options={locations}
                             labelKey="name" valueKey="code"
                             selected={editForm.allowed_locations}
                             onChange={(v) => setEditForm({ ...editForm, allowed_locations: v })} />
                  <MultiChip label="Allowed salesmen" testId="admin-edit-salesmen" options={salesmen}
                             labelKey="name" valueKey="code"
                             selected={editForm.allowed_salesmen}
                             onChange={(v) => setEditForm({ ...editForm, allowed_salesmen: v })} />
                  <PermissionMatrix
                    testId="admin-edit-perms"
                    value={editForm.module_permissions}
                    onChange={(v) => setEditForm({ ...editForm, module_permissions: v })}
                  />
                </>
              )}
            </div>
            <div className="p-5 border-t hairline sticky bottom-0 bg-white flex gap-2 justify-end">
              <button
                data-testid="admin-edit-cancel"
                onClick={() => { setEditing(null); setEditForm(null); }}
                className="text-xs px-4 py-2.5 border border-border rounded-sm uppercase tracking-wider font-semibold text-muted-foreground hover:text-myb-500 hover:border-myb-500"
              >Cancel</button>
              <button
                data-testid="admin-edit-save"
                onClick={saveEdit}
                className="inline-flex items-center gap-2 text-xs px-5 py-2.5 bg-myb-500 text-white hover:bg-myb-600 rounded-sm uppercase tracking-wider font-semibold"
              ><FloppyDisk size={14} /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
